/**
 * Lead capture for the free tools.
 *
 * Every gate on the site funnels through here so there is exactly one place
 * that knows the endpoint, the slug vocabulary and the attribution fields.
 * Slugs — not the human tool names the gates display — are what gets stored,
 * because "which magnet converts" is only a queryable question if the tool
 * column is stable. The edge function still folds the old display strings onto
 * these slugs for browsers running a cached bundle.
 */

export const TOOL_SLUGS = {
  yieldOnCost: "yield-on-cost",
  dividendVsGrowth: "dividend-vs-growth",
  paycheckPlanner: "paycheck-planner",
  freedomNumber: "freedom-number",
  dripSnowball: "drip-snowball",
  taxDrag: "tax-drag",
  cutStressTest: "cut-stress-test",
  // Not a calculator: the Facebook storm-ad landing page at /go. It rides the
  // same capture path so its leads land in the same table and get the same
  // Substack subscribe.
  stormLanding: "storm-landing",
} as const;

export type ToolSlug = (typeof TOOL_SLUGS)[keyof typeof TOOL_SLUGS];

export interface CaptureAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /** The ad code — S1-S10 for the storm ads. */
  utm_content?: string;
  referrer?: string;
}

/**
 * Pull campaign tags off the current URL and the referring page. Read at submit
 * time from whatever the caller passes in (the real `location`/`document` in
 * the app, fakes in tests) rather than reaching for globals directly.
 */
export function readAttribution(
  search: string,
  referrer: string
): CaptureAttribution {
  const params = new URLSearchParams(search);
  const attribution: CaptureAttribution = {};

  const source = params.get("utm_source");
  const medium = params.get("utm_medium");
  const campaign = params.get("utm_campaign");
  const content = params.get("utm_content");

  if (source) attribution.utm_source = source;
  if (medium) attribution.utm_medium = medium;
  if (campaign) attribution.utm_campaign = campaign;
  if (content) attribution.utm_content = content;
  // Same-origin referrers are just internal navigation and say nothing about
  // where the lead came from.
  if (referrer && !referrer.startsWith(window.location.origin)) {
    attribution.referrer = referrer;
  }

  return attribution;
}

// Pinned to the database's region (us-east-1). Left to choose, Supabase runs
// the function near the reader, and from Asia or Europe every database call it
// makes then crosses an ocean. A query parameter, not the x-region header, so
// the browser's permission check needs nothing new.
const CAPTURE_PATH = "/functions/v1/capture-tool-email?forceFunctionRegion=us-east-1";

/**
 * POST a captured email. Throws with a user-showable message on failure — the
 * gates render it verbatim, so it must never leak an endpoint or a status code
 * a reader can do nothing with.
 */
export async function captureToolEmail(
  email: string,
  tool: ToolSlug,
  extra: {
    landing_variant?: string;
    /** The hidden trap field's value; empty for a person. */
    website?: string;
    /** Cloudflare Turnstile's token, from the ad page only. */
    turnstile_token?: string | null;
  } = {}
): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${supabaseUrl}${CAPTURE_PATH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      email,
      tool,
      ...readAttribution(window.location.search, document.referrer),
      ...extra,
    }),
  });

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error("Too many attempts. Please try again in a few minutes.");
    }
    // A 400 carries a message written for the reader ("Please use a permanent
    // email address."), so it is shown as sent.
    if (res.status === 400) {
      const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
      if (typeof body?.error === "string") throw new Error(body.error);
    }
    throw new Error("Failed to save. Please try again.");
  }
}

/**
 * Wakes the capture function without sending anything. Edge functions sleep
 * between requests and the first one after a quiet spell pays about a second
 * to boot, twice over with the browser's permission check. Sent as the ad
 * page opens, that second is spent while the reader types. Failures are
 * ignored: the sign-up itself still works, just slower.
 */
export function warmCaptureFunction(): void {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  fetch(`${supabaseUrl}${CAPTURE_PATH}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${supabaseAnonKey}` },
  }).catch(() => {});
}
